import { gql } from "@apollo/client";
import {useQuery} from '@apollo/client/react';
import { useAuthContext } from '@/context/AuthContext';
import {useAppSelector} from '@/store/hooks.js';

const GET_INVESTMENT_VEHICLES = gql`
  query InvestmentVehicles($companyUuid: ID!) {
    investmentVehicles(companyUuid: $companyUuid) {
      uuid
      companyUuid
      name {
        labelEn
        labelFr
      }
      description {
        labelEn
        labelFr
      }
      type
      generalTermsAndConditionsFileUuid
      riskDisclosureFileUuid
      expectedAnnualReturnRate
      contractDurationInMonths
      interestPayoutFrequencyInMonths
      entryFeePercentage
      managementFee
      managementFeeType
      managementFeePeriod
      minimumInvestmentAmount
      maximumInvestmentAmount
      riskLevel
      currency
      status
      autoRenewable
      earlyWithdrawalAllowed
      capitalGuaranteed
      historicalAverageReturnRate
      interestDistributionType
      interestCalculationMethod
      consultantProfileUuids
      latestConsultantInvestments {
        uuid
        status
      }
      latestDailyValues {
        uuid
        valueDate
        valueInCurrency
        valueInUnit
        dailyValueVariationInCurrency
        dailyValueVariationInUnit
      }
    }
  }
`;

const MOCK_DATA = [
    {
        uuid: "1",
        companyUuid: "company1",
        name: {
            labelFr: "Fonds Sécurisé",
            labelEn: "Secure Fund"
        },
        description: {
            labelFr: "Investissement sécurisé à faible risque",
            labelEn: "Secure low-risk investment"
        },
        type: "BOND_COUPON",
        generalTermsAndConditionsFileUuid: "gtc1",
        riskDisclosureFileUuid: "rd1",
        expectedAnnualReturnRate: 5,
        contractDurationInMonths: 12,
        interestPayoutFrequencyInMonths: 3,
        entryFeePercentage: 1,
        managementFee: 0.5,
        managementFeeType: "PERCENTAGE",
        managementFeePeriod: "QUARTERLY",
        minimumInvestmentAmount: 10000,
        maximumInvestmentAmount: 100000,
        riskLevel: "LOW",
        currency: "EUR",
        status: "ACTIVE",
        autoRenewable: true,
        earlyWithdrawalAllowed: false,
        capitalGuaranteed: true,
        historicalAverageReturnRate: 4.8,
        interestDistributionType: "CAPITALIZED",
        interestCalculationMethod: "COMPOUND",
        consultantProfileUuids: ["profile1", "profile2"],
        latestConsultantInvestments: [
            { uuid: "inv1", status: "ACTIVE" },
            { uuid: "inv2", status: "COMPLETED" }
        ],
        latestDailyValues: [
            {
                uuid: "dv1",
                valueDate: "2024-01-15",
                valueInCurrency: 102.5,
                valueInUnit: 1.025,
                dailyValueVariationInCurrency: 0.1,
                dailyValueVariationInUnit: 0.001
            }
        ]
    },
    {
        uuid: "2",
        companyUuid: "company1",
        name: {
            labelFr: "Fonds Équilibré",
            labelEn: "Balanced Fund"
        },
        description: {
            labelFr: "Investissement équilibré avec potentiel de croissance",
            labelEn: "Balanced investment with growth potential"
        },
        type: "BOND_COUPON",
        generalTermsAndConditionsFileUuid: "gtc2",
        riskDisclosureFileUuid: "rd2",
        expectedAnnualReturnRate: 8,
        contractDurationInMonths: 24,
        interestPayoutFrequencyInMonths: 6,
        entryFeePercentage: 2,
        managementFee: 1,
        managementFeeType: "PERCENTAGE",
        managementFeePeriod: "MONTHLY",
        minimumInvestmentAmount: 25000,
        maximumInvestmentAmount: 500000,
        riskLevel: "MEDIUM",
        currency: "EUR",
        status: "ACTIVE",
        autoRenewable: false,
        earlyWithdrawalAllowed: true,
        capitalGuaranteed: false,
        historicalAverageReturnRate: 7.5,
        interestDistributionType: "DISTRIBUTED",
        interestCalculationMethod: "COMPOUND_WITH_REINVESTMENT",
        consultantProfileUuids: ["profile1"],
        latestConsultantInvestments: [
            { uuid: "inv3", status: "ACTIVE" }
        ],
        latestDailyValues: [
            {
                uuid: "dv2",
                valueDate: "2024-01-15",
                valueInCurrency: 104.2,
                valueInUnit: 1.042,
                dailyValueVariationInCurrency: 0.2,
                dailyValueVariationInUnit: 0.002
            }
        ]
    }
];

const useAllInvestmentVehicules = () => {
    const { hasPrivileges } = useAuthContext();
    const user = useAppSelector((state) => state.auth.currentUser);

    // Return null if user doesn't have ADMIN_INVESTMENT privilege
    if (!hasPrivileges('ADMIN_INVESTMENT')) {
        return {
            loading: false,
            error: new Error('Unauthorized'),
            data: null,
            refetch: () => {}
        };
    }

    const { loading, error, data, refetch } = useQuery(GET_INVESTMENT_VEHICLES, {
        variables: { companyUuid: user.company.uuid },
        fetchPolicy: "network-only",
    });

    // Return mock data in case of error only if mock data is enabled
    if (error && window.ENV.ENABLED_MOCK_DATA) {
        return {
            loading: false,
            error: null,
            data: MOCK_DATA,
            refetch
        };
    }

    return {
        loading,
        error,
        data: data?.investmentVehicles,
        refetch
    };
};

export default useAllInvestmentVehicules;
